/**
 * Singleton binding seam for chat orchestration actions.
 *
 * The composition root (`main.tsx`) calls `configureChatActions()` once at
 * app boot, providing implementations of `launchAgentWithWidget` and
 * `sendOrCreateChat` from `@/features/chats`. Lower-tier code (other
 * features that need to trigger chat actions) then calls
 * `getChatActions()` to read them WITHOUT importing `@/features/chats`
 * directly.
 *
 * Why: features must NOT cross-import sibling features (FSD layer
 * order). Without this seam, e.g. `features/audiences` triggering an
 * audience-interview launch would create a `features/audiences →
 * features/chats` edge — a runtime layer-order inversion.
 *
 * Pattern mirrors `entities/agent/lib/agent-metadata-bindings.ts`,
 * `entities/widget/lib/widget-store-bindings.ts`, and
 * `lib/workspace-context/workspace-context-bindings.ts`.
 */

import type { NavigateFunction } from 'react-router';

interface ChatActionsSendOrCreateChatParams {
  agentId: string;
  prompt: string;
  inputs?: Record<string, unknown>;
  isHidden?: boolean;
}

export interface ChatActionsBindings {
  launchAgentWithWidget: (
    navigate: NavigateFunction,
    agentId: string,
    inputs?: Record<string, unknown>,
    customWidgetData?: Record<string, unknown>,
  ) => void;
  sendOrCreateChat: (
    navigate: NavigateFunction,
    params: ChatActionsSendOrCreateChatParams,
  ) => Promise<void>;
  /**
   * React hook returning whether the AI/chat pipeline is currently busy.
   * Consumers (e.g. `widgets/app-shell/AppSidebar`) read this to gate
   * actions like workspace switching while a generation is in flight.
   * Hook semantics — must be called at the top level of a component or
   * other hook, not inside an event handler.
   */
  useIsChatBusy: () => boolean;
}

let bindings: ChatActionsBindings | null = null;

/**
 * Configures the chat-actions bindings. Called once at app boot from the
 * composition root. Safe to call multiple times — subsequent calls
 * replace the bindings (used by tests via `configureChatActionsForTests`).
 */
export const configureChatActions = (next: ChatActionsBindings): void => {
  bindings = next;
};

/**
 * Test-only reset. Re-applies the bindings or clears them. Production
 * code MUST NOT call this — use `configureChatActions` once at boot.
 */
export const configureChatActionsForTests = (next: ChatActionsBindings | null): void => {
  bindings = next;
};

/**
 * Returns the bound chat-actions helpers. Throws fail-fast if called
 * before `configureChatActions()` runs at app boot — this would
 * indicate a wiring bug (chat actions are essential to the app shell).
 */
export const getChatActions = (): ChatActionsBindings => {
  if (bindings === null) {
    throw new Error(
      '[lib/chat-actions] Chat-actions bindings not configured. ' +
        'Call `configureChatActions` at app boot before any consumer ' +
        'invokes `getChatActions()`.',
    );
  }
  return bindings;
};
